import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, useRole } from "@/context/AuthContext";
import {
  Home,
  User,
  LogOut,
  Menu,
  X,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useState } from "react";
import clsx from "clsx";
import SidebarFooter from "./SidebarFooter";
import {
  FEATURES,
  type FeatureCard,
} from "@/constants/features";

// ═══════════════════════════════════════════════════════════════════
// Phase 1E.1 UI polish — minimalist sidebar shell.
//
// Replaces the top-header layout from Commit 1 with a left sidebar
// matching the reference aesthetic: icon + label per nav item,
// optional count chip, social/email/clock at the bottom. Primary
// content area gets a clean white background with lots of whitespace.
//
// Behavior intentionally unchanged from the prior Layout -- pure
// visual restructure. Mobile drawer pattern preserved.
// ═══════════════════════════════════════════════════════════════════

interface NavItem {
  path: string;
  label: string;
  icon?: LucideIcon;
  /** When set, shows a soft pill with the count. */
  count?: number;
  /** Highlight style — green for "live count", gray for static. */
  countTone?: "green" | "gray";
  match?: (p: string) => boolean;
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { isAdmin } = useRole();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Live count surfaced as a chip on the "Feature labs" section header.
  const liveCount = FEATURES.filter(
    (f) => f.status === "live" && (f.adminOnly ? isAdmin : true),
  ).length;

  const primary: NavItem[] = [
    { path: "/", label: "Home", icon: Home, match: (p) => p === "/" },
    {
      path: "/profile",
      label: "Profile",
      icon: User,
      match: (p) => p === "/profile",
    },
  ];

  const features = FEATURES.filter(
    (f) => (f.adminOnly ? isAdmin : true) && f.category !== "admin",
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ─── Mobile top bar ─────────────────────────────────────── */}
      <div className="md:hidden sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-4 h-14">
          <Link to="/" className="flex items-center gap-2">
            <Logo />
            <span className="font-display font-semibold text-lg">WeDesi Lab</span>
          </Link>
          <button
            className="p-2 -mr-2 text-gray-600"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* ─── Mobile drawer ──────────────────────────────────────── */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-black/30" onClick={() => setMobileOpen(false)}>
          <aside
            className="absolute left-0 top-0 bottom-0 w-72 bg-white p-5 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <SidebarContent
              primary={primary}
              features={features}
              liveCount={liveCount}
              isAdmin={isAdmin}
              location={location.pathname}
              user={user}
              onLogout={handleLogout}
              onNavigate={() => setMobileOpen(false)}
            />
          </aside>
        </div>
      )}

      <div className="md:flex">
        {/* ─── Desktop sidebar ────────────────────────────────── */}
        <aside className="hidden md:flex md:flex-col w-64 shrink-0 h-screen sticky top-0 bg-white border-r border-gray-200 px-4 py-6">
          <SidebarContent
            primary={primary}
            features={features}
            liveCount={liveCount}
            isAdmin={isAdmin}
            location={location.pathname}
            user={user}
            onLogout={handleLogout}
          />
        </aside>

        {/* ─── Main content ───────────────────────────────────── */}
        <main className="flex-1 min-w-0">
          <div className="max-w-5xl mx-auto px-5 md:px-10 py-6 md:py-10">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function SidebarContent({
  primary,
  features,
  liveCount,
  isAdmin,
  location,
  user,
  onLogout,
  onNavigate,
}: {
  primary: NavItem[];
  features: FeatureCard[];
  liveCount: number;
  isAdmin: boolean;
  location: string;
  user: { name?: string; email?: string; nameIsPlaceholder?: boolean } | null;
  onLogout: () => void;
  onNavigate?: () => void;
}) {
  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="hidden md:flex items-center gap-2.5 px-2.5 mb-7">
        <Logo />
        <span className="font-display font-semibold text-lg text-gray-900">
          WeDesi Lab
        </span>
      </div>

      {/* Primary nav */}
      <nav className="space-y-0.5">
        {primary.map((item) => (
          <NavLink
            key={item.path}
            item={item}
            location={location}
            onClick={onNavigate}
          />
        ))}

        {isAdmin && (
          <NavLink
            item={{
              path: "/admin",
              label: "Admin",
              icon: ShieldCheck,
              match: (p) => p.startsWith("/admin"),
            }}
            location={location}
            tone="amber"
            onClick={onNavigate}
          />
        )}
      </nav>

      {/* Feature labs list — secondary nav of every live feature. */}
      <div className="mt-6">
        <div className="px-2.5 pb-1.5 flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            Feature labs
          </span>
          <CountChip count={liveCount} tone="green" />
        </div>
        <nav className="space-y-0.5">
          {features.map((f) => (
            <FeatureLink
              key={f.id}
              feature={f}
              location={location}
              onClick={onNavigate}
            />
          ))}
        </nav>
      </div>

      {/* Footer */}
      <div className="mt-auto pt-6 border-t border-gray-100">
        {user && (
          <div className="px-2.5 mb-3">
            <div className="text-xs text-gray-500">Signed in as</div>
            <div className="text-sm font-medium text-gray-900 truncate">
              {user.name || user.email}
              {/* The sign-up handle, not a name the member gave. */}
              {user.nameIsPlaceholder && (
                <span className="ml-1 text-xs font-normal text-gray-400">(no name yet)</span>
              )}
            </div>
            <button
              onClick={onLogout}
              className="mt-2 flex items-center gap-2 text-xs text-gray-500 hover:text-gray-800 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign out
            </button>
          </div>
        )}
        <SidebarFooter />
      </div>
    </div>
  );
}

// ─── Small atoms ────────────────────────────────────────────────────

function Logo() {
  return (
    <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center shrink-0">
      <Sparkles className="w-4 h-4 text-white" strokeWidth={2} />
    </div>
  );
}

function NavLink({
  item,
  location,
  tone = "default",
  onClick,
}: {
  item: NavItem;
  location: string;
  tone?: "default" | "amber";
  onClick?: () => void;
}) {
  const isActive = item.match
    ? item.match(location)
    : location === item.path;
  const Icon = item.icon;

  return (
    <Link
      to={item.path}
      onClick={onClick}
      className={clsx(
        "flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm transition-colors",
        isActive
          ? tone === "amber"
            ? "bg-amber-50 text-amber-900 font-medium"
            : "bg-gray-100 text-gray-900 font-medium"
          : "text-gray-700 hover:bg-gray-100",
      )}
    >
      {Icon && (
        <Icon
          className={clsx(
            "w-[18px] h-[18px] shrink-0",
            isActive
              ? tone === "amber"
                ? "text-amber-900"
                : "text-gray-900"
              : "text-gray-500",
          )}
          strokeWidth={1.75}
        />
      )}
      <span className="flex-1 truncate">{item.label}</span>
      {typeof item.count === "number" && (
        <CountChip count={item.count} tone={item.countTone} />
      )}
    </Link>
  );
}

function FeatureLink({
  feature,
  location,
  onClick,
}: {
  feature: FeatureCard;
  location: string;
  onClick?: () => void;
}) {
  const isActive = location.startsWith(feature.path);
  const Icon = feature.icon;
  const isLive = feature.status === "live";

  const content = (
    <>
      <Icon
        className={clsx(
          "w-[18px] h-[18px] shrink-0",
          isActive ? "text-gray-900" : "text-gray-500",
        )}
        strokeWidth={1.5}
      />
      <span
        className={clsx(
          "flex-1 truncate",
          !isLive && "text-gray-400",
        )}
      >
        {feature.title.replace(/\s*\(.*?\)\s*/, "")}
      </span>
      {!isLive && (
        <span className="text-[10px] uppercase tracking-wide text-gray-400">
          Soon
        </span>
      )}
    </>
  );

  if (!isLive) {
    return (
      <div className="flex items-center gap-3 px-2.5 py-1.5 rounded-lg text-sm text-gray-400 cursor-not-allowed">
        {content}
      </div>
    );
  }

  return (
    <Link
      to={feature.path}
      onClick={onClick}
      className={clsx(
        "flex items-center gap-3 px-2.5 py-1.5 rounded-lg text-sm transition-colors",
        isActive
          ? "bg-gray-100 text-gray-900 font-medium"
          : "text-gray-700 hover:bg-gray-100",
      )}
    >
      {content}
    </Link>
  );
}

function CountChip({
  count,
  tone = "gray",
}: {
  count: number;
  tone?: "green" | "gray";
}) {
  return (
    <span
      className={clsx(
        "px-2 py-0.5 text-[11px] font-semibold rounded-full tabular-nums",
        tone === "green"
          ? "bg-green-100 text-green-700"
          : "bg-gray-100 text-gray-500",
      )}
    >
      {count}
    </span>
  );
}
